import {
	AdditiveBlending,
	ConeGeometry,
	Group,
	Mesh,
	MeshBasicMaterial,
	PlaneGeometry,
	RepeatWrapping,
	Sprite,
	SpriteMaterial,
	Vector3,
	Box3,
	Quaternion,
	DoubleSide,
	TorusGeometry,
} from 'three';
import { getUnitCollisionRadius, getUnitPosition } from '../hybrid/coordinates.js';
import { getUnitFx } from '../hybrid/fx-palette.js';
import { UnitPositionTypes } from '../hybrid/types.js';
import { gltfLoader, textureLoader } from './instances.js';
import Explosion from './explosion.js';
import LaserBeam, { getRadialGlowTexture } from './laser-beam.js';
import Shield from './shield.js';
import { MeshLine, MeshLineMaterial } from './three.meshline.js';
import { Vector2 } from 'three';
import UnitDecorations from './unit-decorations.js';

const UNIT_VISUAL_HEIGHT = 0;
const UNIT_SHIP_RADIUS = 0.2;
const UNIT_SHIP_LENGTH = 0.8;
// decorations (corner brackets + bars) are pixel-based box-ui now — see
// unit-decorations.js; only the ship-bounds metrics constants remain here
const UNIT_MARKER_WIDTH = 1.1;
const UNIT_MARKER_HEIGHT = 0.8;
const UNIT_MARKER_PADDING = 1.25;
const SHIP_TYPE_CONSTRUCTOR = '000000000000000003000001';
const SHIP_TYPE_TRANSPORTER = '000000000000000003000002';
const SHIP_TYPE_FIGHTER = '000000000000000003000003';
const _targetWorldPosition = new Vector3();
const _shipBounds = new Box3();
const _shipBoundsCenter = new Vector3();
const _shipBoundsSize = new Vector3();
const _cameraWorldQuaternion = new Quaternion();
const _parentWorldQuaternion = new Quaternion();
const _orbitForward = new Vector3();
const _shipForward = new Vector3(0, 0, 1);
const _unitWorldPosition = new Vector3();
const _cameraWorldPosition = new Vector3();
const _attackerWorldPosition = new Vector3();
const _attackDirection = new Vector3();
const _headingDelta = new Vector3();
const _headingTarget = new Quaternion();

function colorFromNation(unit) {
	const color = unit.nation?.color;
	if (typeof color === 'number') return color;
	if (typeof color === 'string' && color) {
		const parsed = Number(color.replace('#', '0x'));
		if (!Number.isNaN(parsed)) return parsed;
	}
	return 0xcccccc;
}

export function abstractToScene({ x = 0, y = 0, z = 0 }) {
	return { x, y: z + UNIT_VISUAL_HEIGHT, z: y };
}

function loadGltf(path) {
	return new Promise((resolve, reject) => {
		gltfLoader.load(path, resolve, undefined, reject);
	});
}

function prepareDesign(root, { scale = 1, material } = {}) {
	const design = root.clone(true);
	design.scale.set(scale, scale, scale);
	design.traverse(node => {
		node.userData.type = 'unit';
		if (!node.isMesh) return;
		node.geometry = node.geometry.clone();
		node.material = material;
	});
	design.updateMatrixWorld(true);
	return design;
}

function cloneDesign(design, unit) {
	const ship = design.clone(true);
	ship.traverse(node => {
		node.userData.unit = unit;
		node.userData.type = 'unit';
	});
	return ship;
}

function sceneToAbstract({ x = 0, y = 0, z = 0 }) {
	return { x, y: z, z: y };
}

function movementObstacle(entity, unit) {
	if (entity === unit || typeof entity.$collisions !== 'undefined') return null;
	if (typeof entity.r !== 'number') return null;
	if (entity.$type === 'unit') return entity;

	const scenePosition = entity.mesh?.position || entity.position;
	if (!scenePosition) return null;
	return {
		...entity,
		position: entity.$type === 'sun'
			? { x: entity.position.x, y: entity.position.y, z: entity.position.z ?? 0 }
			: sceneToAbstract(scenePosition),
	};
}

export async function loadUnitDesigns() {
	if (Unit.designs[SHIP_TYPE_FIGHTER]) return;
	const texture = textureLoader.load('/assets/gfx/textures/ship.jpg');
	texture.wrapS = RepeatWrapping;
	texture.wrapT = RepeatWrapping;
	const material = new MeshBasicMaterial({ map: texture, side: DoubleSide });

	const [destroyer, ships] = await Promise.all([
		loadGltf('/assets/gfx/spaceships/destroyer.glb'),
		loadGltf('/assets/gfx/spaceships/ships.glb'),
	]);

	Unit.addDesign(SHIP_TYPE_FIGHTER, prepareDesign(destroyer.scene.children[0], {
		scale: 0.025,
		material,
	}));
	Unit.addDesign(SHIP_TYPE_CONSTRUCTOR, prepareDesign(ships.scene.children[1] || ships.scene.children[0], {
		scale: 0.01,
		material,
	}));
	Unit.addDesign(SHIP_TYPE_TRANSPORTER, Unit.designs[SHIP_TYPE_CONSTRUCTOR]);
}

export default class Unit {
	static designs = {};

	static addDesign(id, shape) {
		this.designs[String(id)] = shape;
	}

	constructor(data, game) {
		this.game = game;
		this._id = null;
		this.$type = 'unit';
		this.sector = [0, 0];
		this.position = { type: UnitPositionTypes.SPACE, x: 0, y: 0, z: 0 };
		this.remaining = {};
		this.full = {};
		this.r = getUnitCollisionRadius(this);
		this.isMoving = false;

		this.mesh = new Group();
		this.mesh.name = 'UNIT';
		this.mesh.userData.unit = this;

		const design = Unit.designs[String(data.type)];
		this.ship = design
			? cloneDesign(design, this)
			: new Mesh(
				new ConeGeometry(UNIT_SHIP_RADIUS, UNIT_SHIP_LENGTH, 3),
				new MeshBasicMaterial({ color: colorFromNation(data) }),
			);
		this.ship.userData.unit = this;
		this.mesh.add(this.ship);
		this.fx = getUnitFx(data);
		this.laserBeam1 = new LaserBeam(this.mesh, { color: this.fx.weapon });
		// pixel-constant corner brackets + bars in the dedicated decorations
		// layer (outside the world zoom) — see unit-decorations.js
		this.decorations = new UnitDecorations(this);
		game.sectorMap?.decorationsLayer?.add(this.decorations.object3d);
		this.engineGlow = this.createEngineGlow();

		this.update(data);
	}

	createEngineGlow() {
		const glow = new Sprite(new SpriteMaterial({
			map: getRadialGlowTexture(),
			color: this.fx.engine,
			blending: AdditiveBlending,
			transparent: true,
			depthWrite: false,
		}));
		glow.name = 'ENGINE_GLOW';
		glow.userData.unit = this;
		glow.userData.type = 'unit';
		// measure the hull while the ship still has identity orientation
		// (constructor time), so min.z IS the stern of the look frame — the
		// glow must kiss the hull, not hover half a ship-length behind it
		this.mesh.updateMatrixWorld(true);
		this.ship.updateMatrixWorld(true);
		_shipBounds.setFromObject(this.ship);
		_shipBounds.getCenter(_shipBoundsCenter);
		_shipBounds.getSize(_shipBoundsSize);
		// the glow inherits the design root's scale (e.g. 0.025 on gltf ships) —
		// divide it back out so sizes are in world units
		const shipScale = this.ship.scale.x || 1;
		const scale = Math.max(0.15, _shipBoundsSize.y * 1.2) / shipScale;
		glow.userData.baseScale = scale;
		glow.scale.setScalar(scale);
		glow.renderOrder = 790; // below shield bubbles and markers
		glow.position.set(
			_shipBoundsCenter.x / shipScale,
			_shipBoundsCenter.y / shipScale,
			(_shipBounds.min.z / shipScale) - scale * 0.25,
		);
		glow.visible = false;
		this.ship.add(glow);
		return glow;
	}

	getMarkerMetrics() {
		// the glow sprite sits behind the stern and would inflate the bounds
		const glow = this.engineGlow;
		glow?.removeFromParent();
		this.mesh.updateMatrixWorld(true);
		this.ship.updateMatrixWorld(true);
		_shipBounds.setFromObject(this.ship);
		if (glow) this.ship.add(glow);
		_shipBounds.getCenter(_shipBoundsCenter);
		_shipBounds.getSize(_shipBoundsSize);
		this.mesh.worldToLocal(_shipBoundsCenter);

		const width = Math.max(UNIT_MARKER_WIDTH, Math.max(_shipBoundsSize.x, _shipBoundsSize.z) * UNIT_MARKER_PADDING);
		const height = Math.max(UNIT_MARKER_HEIGHT, _shipBoundsSize.y * UNIT_MARKER_PADDING);
		return {
			center: _shipBoundsCenter.clone(),
			width,
			height,
		};
	}

	setOrbitFrame(localSurfacePosition) {
		this.mesh.quaternion.identity();
		_orbitForward.copy(localSurfacePosition).normalize();
		this.ship.quaternion.setFromUnitVectors(_shipForward, _orbitForward);
	}

	update(data) {
		Object.assign(this, data);
		if (!data.course) delete this.course;
		if (!data.destination) delete this.destination;
		if (!data.attacking) delete this.attacking;
		this.$type = 'unit';
		this.r = getUnitCollisionRadius(this);
		this.isMoving = Boolean(this.destination);
		this.updateMarkerColor();
		this.fx = getUnitFx(this);
		this.laserBeam1?.setColor(this.fx.weapon);
		this.shield?.setColor(this.fx.shield);
		if (this.engineGlow) this.engineGlow.material.color.setHex(this.fx.engine);
		this.updateRemaining(this.remaining);
		this.updatePosition();
	}

	updateRemaining(remaining) {
		this.remaining = remaining;
		const shieldRatio = this.full.shield ? this.remaining.shield / this.full.shield : 0;
		const hullRatio = this.full.hull ? this.remaining.hull / this.full.hull : 0;
		this.decorations.setBars(shieldRatio, hullRatio);
		this.shield?.setLife(shieldRatio);
	}

	createShield() {
		const metrics = this.getMarkerMetrics();
		const radius = Math.max(0.6, Math.max(metrics.width, metrics.height) * 0.55);
		this.shield = new Shield({ radius, color: this.fx.shield });
		this.shield.mesh.position.copy(metrics.center);
		this.shield.setLife(this.full.shield ? this.remaining.shield / this.full.shield : 0);
		this.mesh.add(this.shield.mesh);
		return this.shield;
	}

	// called when someone fires at us — flare the bubble where the beam lands.
	// The delay syncs the flare with the beam's travel time.
	onIncomingFire(attacker) {
		if (!this.full.shield || !(this.remaining.shield > 0)) return;
		if (!this.shield) this.createShield();
		attacker.mesh.getWorldPosition(_attackerWorldPosition);
		this.shield.registerHitFromWorld(_attackerWorldPosition, 0.24);
	}

	async explode() {
		this.explosion = new Explosion(this.mesh, 500);
		await this.explosion.promise;
		this.laserBeam1?.dispose();
		this.decorations?.dispose();
		this.game.entities.remove(this);
	}

	execAttack(target) {
		this.laserBeam1.fireAt(this.getAttackPoint(target), () => this.getAttackPoint(target));
	}

	// where this unit's beam should end on `target`, in scene coords: the
	// shield bubble's surface while it is up, the hull otherwise. Re-evaluated
	// per frame by the beam so a collapse mid-flight extends it to the hull.
	getAttackPoint(target) {
		if (target.orbit) {
			target.mesh.getWorldPosition(_targetWorldPosition);
			this.game.main.worldToLocal(_targetWorldPosition);
		} else {
			const p = abstractToScene(target.position);
			_targetWorldPosition.set(p.x, p.y, p.z);
		}
		const shield = target.shield;
		if (shield && shield.state !== 'down' && target.remaining?.shield > 0) {
			const radius = shield.mesh.scale.x;
			// own position in scene coords too — this.mesh.position would be
			// planet-local when this unit sits in orbit
			this.mesh.getWorldPosition(_attackerWorldPosition);
			this.game.main.worldToLocal(_attackerWorldPosition);
			_attackDirection.copy(_targetWorldPosition).sub(_attackerWorldPosition);
			const distance = _attackDirection.length();
			if (distance > radius) {
				_attackDirection.multiplyScalar((distance - radius) / distance);
				_targetWorldPosition.copy(_attackerWorldPosition).add(_attackDirection);
			}
		}
		return _targetWorldPosition;
	}

	updatePosition() {
		if (this.position.type === UnitPositionTypes.ORBIT) return;
		const p = abstractToScene(this.position);
		this.mesh.position.set(p.x, p.y, p.z);
	}

	// eases the mesh toward the fixed-step sim position — cosmetic only, the
	// authoritative position is always this.position
	updateVisualPosition() {
		if (this.position.type === UnitPositionTypes.ORBIT) return;
		const p = abstractToScene(this.position);
		const now = Date.now();
		const dt = Math.min(200, now - (this._lastVisualTime ?? now));
		this._lastVisualTime = now;
		const alpha = 1 - Math.exp(-dt / 60);
		this.mesh.position.x += (p.x - this.mesh.position.x) * alpha;
		this.mesh.position.y += (p.y - this.mesh.position.y) * alpha;
		this.mesh.position.z += (p.z - this.mesh.position.z) * alpha;
	}

	getSelectableObjects() {
		return [this.mesh, this.decorations.object3d];
	}

	setSelected(selected) {
		this.selected = selected;
		this.updateMarkerColor();
		this.setRangeRingVisible(selected);
	}

	// pointer (or XR controller ray) hovering the marker rectangle
	setHovered(hovered) {
		this.hovered = hovered;
		this.updateMarkerColor();
	}

	// corners: hover yellow; enemy red and ally blue (always); own/neutral
	// white, green when selected. Viewer identity: localStorage.playerNationId
	// (dev shim until login binds a nation)
	updateMarkerColor() {
		if (this.hovered) return this.decorations.setState('yellow');
		const relation = this.getRelationToViewer();
		let color = 'white';
		if (relation === 'enemy') color = 'red';
		else if (relation === 'ally') color = 'blue';
		else if (this.selected) color = 'green';
		this.decorations.setState(color);
	}

	getRelationToViewer() {
		let viewer = null;
		try { viewer = localStorage.getItem('playerNationId'); } catch { /* storage blocked */ }
		viewer ||= globalThis.playerNationId;
		if (!viewer || !this.nation?._id) return 'unknown';
		if (String(this.nation._id) === String(viewer)) return 'own';
		const relation = this.nation.relations?.[String(viewer)];
		if (relation === 'hostile') return 'enemy';
		if (relation === 'ally') return 'ally';
		return 'neutral';
	}

	setRangeRingVisible(visible) {
		if (visible && !this.rangeRing) {
			// constant screen-width line — a world-unit tube was sub-pixel
			// (invisible) at fleet zoom, exactly where band selection is used
			const radius = this.weaponsRange ?? 5;
			const points = [];
			for (let i = 0; i <= 64; i++) {
				const a = (i / 64) * Math.PI * 2;
				points.push(new Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius));
			}
			const line = new MeshLine();
			line.setPoints(points);
			this.rangeRing = new Mesh(line, new MeshLineMaterial({
				resolution: new Vector2(
					this.game.container?.clientWidth || window.innerWidth,
					this.game.container?.clientHeight || window.innerHeight,
				),
				color: colorFromNation(this),
				lineWidth: 3,
				sizeAttenuation: 0,
				transparent: true,
				opacity: 0.85,
			}));
			this.rangeRing.renderOrder = 903;
			this.rangeRing.frustumCulled = false;
			this.mesh.add(this.rangeRing);
		} else if (!visible && this.rangeRing) {
			this.mesh.remove(this.rangeRing);
			this.rangeRing.geometry.dispose();
			this.rangeRing.material.dispose();
			delete this.rangeRing;
		}
	}

	// position/billboard/scale the decoration in its own layer so 1 box unit
	// = 1 screen pixel — same formula the map labels use
	updateDecorations(camPos, camQuat, tanHalfFov, screenH, isXR) {
		const o = this.decorations.object3d;
		this.mesh.getWorldPosition(_unitWorldPosition);
		this.game.main.worldToLocal(_unitWorldPosition);
		o.position.copy(_unitWorldPosition);
		if (isXR) {
			o.rotation.set(0, Math.atan2(camPos.x - o.position.x, camPos.z - o.position.z), 0);
		} else {
			o.quaternion.copy(camQuat);
		}
		const dist = camPos.distanceTo(o.position);
		o.scale.setScalar((2 * dist * tanHalfFov) / screenH);
	}

	async render() {
		if (this.isMoving && this.destination) {
			const obstacles = this.game.entities
				.getSectorAndNeighbors(this.sector)
				.map(entity => movementObstacle(entity, this))
				.filter(Boolean);
			if (await getUnitPosition(this, obstacles)) {
				this.isMoving = false;
				if (this.position.type === UnitPositionTypes.ORBIT) {
					await this.game.sectorMap.placeUnit(this, this);
					return;
				}
				this.updatePosition();
			} else {
				this.updateVisualPosition();
			}
			this.updateHeading();
		}
		if (this.laserBeam1.active) this.laserBeam1.render();
		if (this.explosion?.render()) delete this.explosion;
		if (this.engineGlow && this.engineGlow.visible !== this.isMoving) {
			this.engineGlow.visible = this.isMoving;
		}
		if (this.engineGlow?.visible) {
			const pulse = this.engineGlow.userData.baseScale * (1 + 0.15 * Math.sin(performance.now() / 90));
			this.engineGlow.scale.setScalar(pulse);
		}
		if (this.shield?.mesh.visible || this.shield?.pendingHits.length) this.shield.update();
	}

	setDirection(target) {
		if (!target) return;
		const p = abstractToScene(target);
		this.ship.lookAt(p.x, p.y, p.z);
		// (a call to an undefined updateMarkerAnchor() sat here since fc0936c and threw on every
		// heading update of a moving unit — "unit render failed" per frame; the marker is placed
		// per frame by updateDecorations anyway)
	}

	// the nose follows the ACTUAL travel direction (avoidance slides included)
	// and eases around instead of snapping — pointing at the ordered course
	// while sliding along another unit's collision radius looked broken
	updateHeading() {
		if (this.position.type === UnitPositionTypes.ORBIT) return;
		const p = this.mesh.position;
		if (!this._lastHeadingPosition) {
			this._lastHeadingPosition = p.clone();
			if (this.destination) this.setDirection(this.destination);
			return;
		}
		_headingDelta.copy(p).sub(this._lastHeadingPosition);
		this._lastHeadingPosition.copy(p);
		if (_headingDelta.lengthSq() < 1e-8) return;
		_headingTarget.copy(this.ship.quaternion);
		this.ship.lookAt(
			p.x + _headingDelta.x * 100,
			p.y + _headingDelta.y * 100,
			p.z + _headingDelta.z * 100,
		);
		// ship.quaternion now holds the velocity heading; ease toward it — per TIME, not per frame
		// (0.85 kept per 60 Hz frame; a 20 fps phone turned three times slower)
		const now = performance.now();
		const dt = Math.min(200, now - (this._lastHeadingTime ?? now - 1000 / 60));
		this._lastHeadingTime = now;
		this.ship.quaternion.slerp(_headingTarget, Math.pow(0.85, dt / (1000 / 60)));
	}

	dispose() {
		this.setRangeRingVisible(false);
		this.mesh.geometry?.dispose();
		this.laserBeam1?.dispose();
		this.shield?.dispose();
		this.engineGlow?.material.dispose();
		this.decorations?.dispose();
		this.ship.geometry?.dispose();
		this.ship.material?.dispose();
	}
}
